import {
  TableSortLabel,
  Paper,
  TablePagination,
  Pagination,
} from "@mui/material";
import React, { useEffect } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useSelector } from "react-redux";
import {
  setPageTopKeyword,
  setRowsPerPageTopKeyword,
  setSortTopKeyword,
} from "../../../../../features/landingPageExperienceSlice";
import { useDispatch } from "react-redux";
import { Stack } from "@mui/system";
import { formatCurrency } from "../../../../../utils/helpers";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";

interface Props {
  tableHeadings: { [key: string]: string };
  records: any[];
}

const TopKeywordsTable = ({ tableHeadings, records }: Props) => {
  const dispatch = useDispatch();
  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    relevantAccountsBasedOnSite,
    pageTopKeyword,
    sortTopKeyword,
    rowsPerPageTopKeyword,
  } = useSelector((state: any) => state?.landingPageExperience);
  const accountFromURL = searchParams.get("account");
  const matchedAccounts =
    relevantAccountsBasedOnSite?.length > 0 &&
    relevantAccountsBasedOnSite.find(
      (account: any) => account.account_id === accountFromURL
    );

  const headCells = Object.entries(tableHeadings);
  const navigate = useNavigate();
  const location = useLocation();

  // headers for the table columns with sortable option for each column
  const tableheaders = [
    { id: 1, KEY: "keyword_name", label: "KeyWord", sortable: true },
    {
      id: 2,
      KEY: "matchtype", // Use the nested key
      label: "Match Type",
      sortable: true,
    },
    { id: 3, KEY: "traffic_source", label: "Traffic Source", sortable: true },
    { id: 4, KEY: "adgroup_name", label: "Ad Group", sortable: true },
    { id: 5, KEY: "campaign_name", label: "Campaign", sortable: true },
    {
      id: 6,
      KEY: "lp_exp_score_weight",
      label: "LP Experience",
      sortable: true,
    },
    { id: 7, KEY: "cost", label: "Cost", sortable: true },
    { id: 8, KEY: "quality_score_weight", label: "QS", sortable: true },
  ];

  //-- Sorting Data
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      //default sorting with hidden score desc
      if (!sortTopKeyword.keytoSort || !sortTopKeyword.direction) {
        return arrayToSort
          .map((item) => ({
            ...item,
            score: Math.round(item.cost) * (item.lp_exp_score_weight - 2), // hidden score
          }))
          .slice()
          .sort((a, b) => b.score - a.score);
      }

      // if (!sortTopKeyword.keytoSort || !sortTopKeyword.direction) {
      //   return arrayToSort;
      // }

      // Custom sort based on key and direction
      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sortTopKeyword.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue = a[keytoSort];
        let bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortTopKeyword.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sortTopKeyword.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        } else {
          return sortTopKeyword.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sortTopKeyword]
  );

  // -- Function to handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPageTopKeyword(newPage));
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("pageTopKeyword", newPage.toString());
    setSearchParams(updatedParams);
  };

  //-- Function to handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPageTopKeyword(+event.target.value));
    dispatch(setPageTopKeyword(0));
  };

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    dispatch(
      setSortTopKeyword({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sortTopKeyword.keytoSort &&
          sortTopKeyword.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  useEffect(() => {
    const sortedRows = getSortedData(records || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [records, getSortedData]);

  useEffect(() => {
    dispatch(
      setPageTopKeyword(Number(searchParams.get("pageTopKeyword")) || 0)
    );
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("pageTopKeyword")); // Convert to number
    dispatch(setPageTopKeyword(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
    // eslint-disable-next-line
  }, [searchParams]);

  const handleKeywordClick = (row: any) => {
    // Get existing search params
    const searchParams = new URLSearchParams(location.search);

    // Add keyword_internal_id
    searchParams.set("keyword_internal_id", row?.keyword_internal_id);

    // Calculate last 30 days range
    const endDate = dayjs().format("YYYY-MM-DD");
    const startDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");

    // Add start_date and end_date
    searchParams.set("start_date", startDate);
    searchParams.set("end_date", endDate);

    // Navigate with preserved + new params
    navigate(
      `/paidTraffic/methodology/landing_page_experience/analysis?${searchParams.toString()}`
    );
  };

  return (
    <>
      <TableContainer
        className="KeywordsTableWrap"
        component={Paper}
        sx={{ boxShadow: "none" }}
      >
        <Table size="medium" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header: any) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={
                      sortTopKeyword.keytoSort
                        ? sortTopKeyword.keytoSort === header.KEY
                        : header.KEY === "lp_exp_score_weight" ||
                          header.KEY === "cost" // default sort column
                    }
                    direction={
                      sortTopKeyword.keytoSort &&
                      sortTopKeyword.keytoSort === header.KEY
                        ? sortTopKeyword.direction
                        : header.KEY === "cost"
                        ? "desc" // default for cost
                        : header.KEY === "lp_exp_score_weight"
                        ? "desc" // default for lp_exp_score_weight
                        : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData
              .slice(
                pageTopKeyword * rowsPerPageTopKeyword,
                pageTopKeyword * rowsPerPageTopKeyword + rowsPerPageTopKeyword
              )
              .map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell
                        key={cellIndex}
                        align="left"
                        sx={{ py: "3px" }}
                      >
                        {key === "keyword_name" ? (
                          <>
                            <RouterLink
                              data-tracker="442"
                              to="#"
                              style={{
                                textDecoration: "underline",
                                color: "#9C27B0",
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                //e.stopPropagation();
                                handleKeywordClick(row);
                              }}
                            >
                              {row[key] ? row[key] : ""}
                            </RouterLink>
                          </>
                        ) : key === "matchtype" ? (
                          <> {row[key] ? row[key] : ""}</>
                        ) : key === "traffic_source" ? (
                          <> {matchedAccounts?.traffic_source}</>
                        ) : key === "adgroup_name" ? (
                          <> {row[key] ? row[key] : ""}</>
                        ) : key === "campaign_name" ? (
                          <>{row[key] ? row[key] : ""}</>
                        ) : key === "lp_exp_score_weight" ? (
                          <> {row[key] ? row[key].toFixed(2) : ""}</>
                        ) : key === "cost" ? (
                          <> {row[key] ? formatCurrency(row[key]) : ""}</>
                        ) : key === "quality_score_weight" ? (
                          <> {row[key] ? row[key].toFixed(2) : ""}</>
                        ) : (
                          ""
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedData?.length}
            rowsPerPage={rowsPerPageTopKeyword}
            page={pageTopKeyword}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(sortedData?.length / rowsPerPageTopKeyword)}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={pageTopKeyword + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 100]}
            component="div"
            count={sortedData?.length}
            rowsPerPage={rowsPerPageTopKeyword}
            page={pageTopKeyword}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
      </TableContainer>
    </>
  );
};

export default TopKeywordsTable;
